import { ReactNode, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

interface AdminRouteProps {
  children: ReactNode;
  requiredPermission?: string;
}

export default function AdminRoute({ children, requiredPermission }: AdminRouteProps) {
  const { user, loading, isAuthenticated, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectedRef = useRef(false);

  useEffect(() => {
    if (loading || redirectedRef.current) return;

    if (!isAuthenticated()) {
      redirectedRef.current = true;
      const currentPath = location.pathname + location.search;
      if (currentPath.startsWith('/admin') && currentPath !== '/admin/login') {
        sessionStorage.setItem('adminRedirectPath', currentPath);
      }
      // 未登录访问后台：静默跳转登录页，不带 alarm 式 reason，避免登录成功前闪错误提示
      navigate('/admin/login', { replace: true });
    }
  }, [loading, isAuthenticated, navigate, location.pathname, location.search]);

  // 显示加载状态
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">验证登录状态中...</p>
        </div>
      </div>
    );
  }

  // 如果没有认证，返回空（将被重定向到登录页面）
  if (!isAuthenticated() || !user) {
    return null;
  }

  // 如果有权限要求，检查用户权限
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md">
            <div className="text-red-500 text-4xl mb-4">
              <i className="ri-error-warning-line"></i>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">权限不足</h2>
            <p className="text-gray-600 mb-6">
              您没有访问此页面的权限，请联系管理员获取相应权限。
            </p>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              返回上一页
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 认证通过且权限检查通过，渲染子组件
  return <>{children}</>;
}
